import * as React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  InputAdornment,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";

/**
 * Props:
 * - open: boolean
 * - slotId: string | number
 * - slotLabel: string
 * - initialValues?: { nombre?: string, documento?: string }  // para ocupar o para liberar (puede venir prellenado)
 * - onCancel: () => void
 * - onConfirm: (payload: { slotId, slotLabel, nombre, documento }) => void
 */
export default function ReservaUbicacionDialog({
  open,
  title,
  slotId,
  slotLabel,
  visitante,
  editable,
  onCancel,
  onConfirm,
}) {
  const [nombre, setNombre] = React.useState(visitante?.nombre || "");
  const [documento, setDocumento] = React.useState(visitante?.documento || "");
  const [touched, setTouched] = React.useState({ nombre: false, documento: false });

  React.useEffect(() => {
    if (!open) return;
    setNombre(visitante?.nombre || "");
    setDocumento(visitante?.documento || "");
    setTouched({ nombre: false, documento: false });
  }, [open, visitante]);

  // const normalizarDocumento = (v) => v.replace(/[^\d]/g, "");
  const normalizarDocumento = (v) => v.replace(/[^\w]/g, "");

  const errores = React.useMemo(() => {
    const n = nombre.trim();
    const d = documento.trim();

    return {
      nombre: n.length === 0 ? "Ingresá el nombre" : "",
      documento:
        d.length === 0
          ? "Ingresá el documento"
          // : !/^\d{6,12}$/.test(d)
          : !/^\w{6,12}$/.test(d)
          ? "Documento inválido (6 a 12 caracteres)"
          : "",
    };
  }, [nombre, documento]);

  const puedeConfirmar = Boolean(slotId) && !errores.nombre && !errores.documento;

  const handleEscanearDNI = () => {

  }

  const handleConfirmar = () => {
    setTouched({ nombre: true, documento: true });
    if (!puedeConfirmar) return;

    onConfirm?.({
      slotId,
      slotLabel: slotLabel || String(slotId),
      nombre: nombre.trim(),
      documento: documento.trim(),
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      fullWidth
      maxWidth="sm"
      sx={{zIndex: 10}}
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pr: 6 }}>
        {title}
        <IconButton
          onClick={onCancel}
          aria-label="Cerrar"
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Completá los datos del visitante.
          </Typography>

          <TextField
            label="Ubicación"
            value={slotLabel || (slotId != null ? String(slotId) : "")}
            InputProps={{
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  <PlaceOutlinedIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            label="Nombre y apellido"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, nombre: true }))}
            error={touched.nombre && Boolean(errores.nombre)}
            helperText={touched.nombre ? errores.nombre : " "}
            autoFocus
            slotProps={{
              input: {
                readOnly: !!visitante,
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineIcon fontSize="small" />
                  </InputAdornment>
                ),
            }}}
          />

          <TextField
            label="Documento"
            value={documento}
            onChange={(e) => setDocumento(normalizarDocumento(e.target.value))}
            onBlur={() => setTouched((t) => ({ ...t, documento: true }))}
            error={touched.documento && Boolean(errores.documento)}
            helperText={touched.documento ? errores.documento : " "}
            // inputMode="numeric"
            slotProps={{
              input: {
                readOnly: !!visitante,
                startAdornment: (
                  <InputAdornment position="start">
                    <BadgeOutlinedIcon fontSize="small" />
                  </InputAdornment>
                ),
              }
            }}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {!visitante && <Button onClick={handleEscanearDNI} variant="outlined">
          Escanear DNI
        </Button>}
        <Button onClick={onCancel} variant="outlined">
          Cancelar
        </Button>
        <Button onClick={handleConfirmar} disabled={!puedeConfirmar} variant="contained">
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
